import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Divider,
  Tab,
  Tabs,
  Chip,
  Switch,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { styled } from '@mui/material/styles'
import { ModalContent } from './styled'
import { updateStaffMember, updateStaffUserInfo, uploadStaffAvatar, resetPassword } from 'api'
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import KeyIcon from '@mui/icons-material/Key';

// Styled components
const ProfileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
    gap: theme.spacing(2),
  }
}))

const TabPanel = (props) => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  )
}

const StaffDetail = ({ staff, venues = [], onClose, onUpdate, onResendActivation }) => {
  const { t } = useTranslation()
  const [tabValue, setTabValue] = useState(0)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: staff?.user?.first_name || '',
    last_name: staff?.user?.last_name || '',
    email: staff?.user?.email || '',
    role: staff?.role || 'MA',
    status: staff?.status || 'AC',
    phone: staff?.user?.phone || '',
    selectedVenues: staff?.venues?.map(venue => venue.slug) || []
  })
  const [error, setError] = useState(null)

  // File input reference
  const fileInputRef = useRef(null)

  const roleOptions = [
    { value: 'AD', label: 'Admin' },
    { value: 'MA', label: 'Manager' }
  ]

  const statusOptions = [
    { value: 'AC', label: 'Active' },
    { value: 'IN', label: 'Inactive' }
  ]

  const roleMapping = {
    AD: 'Admin',
    MA: 'Manager'
  }

  const statusMapping = {
    AC: 'Active',
    IN: 'Inactive',
    PP: 'Pending'
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleEditClick = () => {
    setEditing(true)
  }

  const handleResendActivation = () => {
    if (!onResendActivation) return;

    setSendingEmail(true);

    onResendActivation(staff.slug)
      .then(() => {
        setSendingEmail(false);
        setEmailSuccess(true);
        setSuccessMessage('Activation email sent successfully');

        // Clear success message after 5 seconds
        setTimeout(() => {
          setEmailSuccess(false);
        }, 5000);
      })
      .catch(error => {
        setSendingEmail(false);
        setError('Failed to send activation email. Please try again.');
        console.error('Error sending activation email:', error);
      });
  };

  const handleResetPassword = () => {
    setSendingEmail(true);

    resetPassword({ email: staff.user?.email })
      .then(() => {
        setSendingEmail(false);
        setEmailSuccess(true);
        setSuccessMessage('Password reset email sent successfully');

        // Clear success message after 2 seconds
        setTimeout(() => {
          setEmailSuccess(false);
        }, 5000);
      })
      .catch(error => {
        setSendingEmail(false);
        setError('Failed to send password reset email. Please try again.');
        console.error('Error sending password reset email:', error);
      });
  };

  // Update the cancel edit function for consistency
  const handleCancelEdit = () => {
    setEditing(false)
    // Reset form data to original values
    setFormData({
      first_name: staff?.user?.first_name || '',
      last_name: staff?.user?.last_name || '',
      email: staff?.user?.email || '',
      role: staff?.role || 'MA',
      status: staff?.status || 'AC',
      phone: staff?.user?.phone || '',
      selectedVenues: staff?.venues?.map(venue => venue.slug) || []
    })
    setError(null)
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleVenueToggle = (venueSlug) => {
    setFormData(prevData => {
      const selectedVenues = [...prevData.selectedVenues]
      if (selectedVenues.includes(venueSlug)) {
        return {
          ...prevData,
          selectedVenues: selectedVenues.filter(slug => slug !== venueSlug)
        }
      } else {
        return {
          ...prevData,
          selectedVenues: [...selectedVenues, venueSlug]
        }
      }
    })
  }

  const handleSaveChanges = () => {
    setSaving(true)
    setError(null)

    // Create a single data object for the combined endpoint
    const updatedData = {
      slug: staff.slug,
      // Add all fields for changes
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone || ''
    }

    // Handle role changes
    updatedData.role = formData.role

    // Handle status changes
    // Prevent changing from Pending (PP) to Active (AC)
    if (staff.status === 'PP' && formData.status === 'AC') {
      setSaving(false)
      setError(t('Staff members must activate their account via email before status can be set to Active'))
      return
    } else {
      updatedData.status = formData.status
    }

    // Check if venues are available
    if (venues && venues.length > 0) {
      // Always include venue_slugs, even if it's an empty array
      updatedData.venue_slugs = formData.selectedVenues || []
    }


    // Make the API call
    updateStaffMember(updatedData)
      .then(response => {
        setSaving(false)
        setEditing(false)
        setSuccessMessage('Staff information updated successfully')
        onUpdate()

        // Call the onUpdate function to refresh the staff list
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate()
        }
      })
      .catch(err => {
        setSaving(false)
        setError('An error occurred while saving changes. Please try again.')
        console.error('Error updating staff:', err)
      })
  }

  const handleAvatarUpload = (file) => {
    setUploading(true)
    uploadStaffAvatar(staff.slug, file)
      .then(response => {
        setUploading(false)
        setSuccessMessage('Avatar updated successfully')

        // Call the onUpdate function to refresh the staff list
        if (onUpdate && typeof onUpdate === 'function') {
          onUpdate()
        }
      })
      .catch(err => {
        setUploading(false)
        setError('Error uploading avatar')
        console.error('Error uploading avatar:', err)
      })
  }

  // Add a useEffect hook to update venues when staff changes
  useEffect(() => {
    if (staff && staff.venues) {
      setFormData(prevData => ({
        ...prevData,
        // Ensure we correctly set the venue slugs
        selectedVenues: staff.venues.map(venue => venue.slug)
      }))
    }
  }, [staff])

  if (!staff) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative', p: { xs: 1, sm: 2 } }}>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          zIndex: 10 // Increased z-index to ensure it stays on top
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Profile Header */}
      <ProfileHeader>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={staff.user?.avatar}
            alt={`${staff.user?.first_name} ${staff.user?.last_name}`}
            sx={{
              width: 100,
              height: 100,
              fontSize: 40,
              cursor: editing ? 'pointer' : 'default',
              '&:hover': editing ? {
                opacity: 0.8,
                '&::after': {
                  content: '"Upload"',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  borderRadius: '50%'
                }
              } : {}
            }}
            onClick={editing ? () => fileInputRef.current?.click() : undefined}
          >
            {staff.user?.first_name
              ? staff.user.first_name.charAt(0)
              : staff.user?.email.charAt(0)}
          </Avatar>
          {editing && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleAvatarUpload(e.target.files[0]);
                  }
                }}
              />
              {uploading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              textAlign: { xs: 'center', sm: 'left' } // Center text on mobile, left-align on desktop
            }}
          >
            {staff.user?.first_name && staff.user?.last_name
              ? `${staff.user.first_name} ${staff.user.last_name}`
              : staff.user?.email.split('@')[0]
            }
          </Typography>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
            justifyContent: { xs: 'center', sm: 'flex-start' } // Center chips on mobile, left-align on desktop
          }}>
            <Chip
              label={t(roleMapping[staff.role])}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={t(statusMapping[staff.status])}
              color={staff.status === 'AC' ? 'success' : staff.status === 'PP' ? 'warning' : 'error'}
              variant="outlined"
            />
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              textAlign: { xs: 'center', sm: 'left' } // Center email on mobile, left-align on desktop
            }}
          >
            {staff.user?.email}
          </Typography>
        </Box>

        {!editing ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignSelf: { xs: 'center', sm: 'flex-start' },
              paddingRight: { xs: 0, sm: '40px' },
              mt: { xs: 2, sm: 0 }
            }}
          >
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
            >
              {t('Edit Profile')}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignSelf: { xs: 'center', sm: 'flex-start' },
              paddingRight: { xs: 0, sm: '40px' },
              mt: { xs: 2, sm: 0 }
            }}
          >
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelEdit}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={saving}
            >
              {t('Save')}
            </Button>
          </Box>
        )}
      </ProfileHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="staff detail tabs"
        >
          <Tab label={t('Profile')} id="staff-tab-0" aria-controls="staff-tabpanel-0" />
          <Tab label={t('Venues')} id="staff-tab-1" aria-controls="staff-tabpanel-1" />
        </Tabs>
      </Box>

      {/* Profile Tab - COMPACT VERSION */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent sx={{ pb: 2 }}>
            {/* Personal Information Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                {t('Personal Information')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {editing ? (
                // Editing mode - compact form layout
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('First Name')}
                      name="first_name"
                      value={formData?.first_name}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('Last Name')}
                      name="last_name"
                      value={formData?.last_name}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('Email')}
                      name="email"
                      value={formData?.email}
                      onChange={handleInputChange}
                      fullWidth
                      disabled
                      size="small"
                      margin="dense"
                      helperText={t('Email cannot be changed')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('Phone')}
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                </>
              ) : (
                // View mode - condensed information display
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('First Name')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {staff.user?.first_name || t('Not provided')}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Last Name')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {staff.user?.last_name || t('Not provided')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Email')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {staff.user?.email}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Phone')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {staff.user?.phone || t('Not provided')}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        <Card>
          <CardContent sx={{ pb: 2 }}>
            {/* Staff Settings Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                {t('Staff Settings')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {editing ? (
                // Editing mode - compact form layout
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label={t('Role')}
                      name="role"
                      value={formData?.role}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      margin="dense"
                    >
                      {roleOptions?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label={t('Status')}
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      margin="dense"
                      disabled={formData.status === 'PP'} // Disable if status is Pending
                      helperText={formData.status === 'PP' ? t('Requires activation') : ''}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {formData.status === 'PP' && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          startIcon={sendingEmail ? <CircularProgress size={20} /> : <EmailIcon />}
                          onClick={handleResendActivation}
                          disabled={sendingEmail || emailSuccess}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          {t('Resend Activation')}
                        </Button>
                        {emailSuccess && (
                          <Typography variant="caption" color="success.main">
                            {t('Email sent')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </>
              ) : (
                // View mode - inline display of information
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Role')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {t(roleMapping[staff.role])}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Registration Date')}:&nbsp;
                      </Typography>
                      <Typography variant="body2" component="span">
                        {new Date(staff.create_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        {t('Status')}:&nbsp;
                      </Typography>
                      <Chip
                        label={t(statusMapping[staff.status])}
                        color={staff.status === 'AC' ? 'success' : staff.status === 'PP' ? 'warning' : 'error'}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                      {staff.status === 'PP' && (
                        <Tooltip title={t('Awaiting account activation')}>
                          <LockIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </Box>

                    {staff.status === 'PP' && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
                          onClick={handleResendActivation}
                          disabled={sendingEmail || emailSuccess}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          {t('Resend Activation')}
                        </Button>
                        {emailSuccess && (
                          <Typography variant="caption" color="success.main">
                            {t('Email sent')}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {staff.status === 'AC' && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          startIcon={sendingEmail ? <CircularProgress size={16} /> : <KeyIcon />}
                          onClick={handleResetPassword}
                          disabled={sendingEmail || emailSuccess}
                          size="small"
                          sx={{ mr: 1 }}
                        >
                          {t('Reset Password')}
                        </Button>
                        {emailSuccess && (
                          <Typography variant="caption" color="success.main">
                            {t('Email sent')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Venues Tab - SIMPLIFIED VIEW */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {editing ? t('Select Venues') : t('Assigned Venues')}
          </Typography>
          {editing && (
            <Typography variant="body2" color="text.secondary">
              {t('Toggle switches to assign venues')}
            </Typography>
          )}
        </Box>

        {!venues || venues.length === 0 ? (
          <Alert severity="info">
            {t('Loading venues...')}
          </Alert>
        ) : (
          <>
            {editing ? (
              // Edit mode: Show all venues for selection
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <List disablePadding>
                  {venues.map((venue) => (
                    <ListItem
                      key={venue.slug}
                      button
                      divider
                      onClick={() => handleVenueToggle(venue.slug)}
                      dense
                      sx={{
                        backgroundColor: formData.selectedVenues.includes(venue.slug)
                          ? 'rgba(0, 0, 0, 0.04)'
                          : 'inherit'
                      }}
                    >
                      <ListItemText
                        primary={venue.name}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          edge="end"
                          checked={formData.selectedVenues.includes(venue.slug)}
                          onChange={() => handleVenueToggle(venue.slug)}
                          color="primary"
                          inputProps={{ 'aria-label': 'venue assignment switch' }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ) : (
              // View mode: Only show assigned venues
              <>
                {formData.selectedVenues.length > 0 ? (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <List disablePadding>
                      {venues
                        .filter(venue => formData.selectedVenues.includes(venue.slug))
                        .map((venue) => (
                          <ListItem
                            key={venue.slug}
                            divider
                            dense
                          >
                            <ListItemText primary={venue.name} />
                          </ListItem>
                        ))}
                    </List>
                  </Paper>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {t('No venues assigned to this staff member')}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </TabPanel>
    </Box>
  )
}

export default StaffDetail